import { useEffect, useState } from "react";

import { OrganizationsService } from "@/modules/dashboard/cards/services/organizationsService";
import type { Organization } from "@/modules/dashboard/cards/types/organizations-api.types";

export interface UseSelectedOrganizationResult {
  organization: Organization | null;
  isLoading: boolean;
}

/**
 * The organization behind the picked id.
 *
 * The picker reaches every organization through the server-side search, so the chosen one is often outside
 * the capped page the catalog preloaded — and this screen needs its `card_provider` before it may read the
 * wallet. Resolved from the catalog when it is there (free) and read by id when it is not.
 */
export function useSelectedOrganization(
  organizationId: string | null,
  catalog: Organization[],
): UseSelectedOrganizationResult {
  const fromCatalog = organizationId
    ? (catalog.find((organization) => organization.id === organizationId) ?? null)
    : null;

  const [fetched, setFetched] = useState<{ id: string; organization: Organization } | null>(null);
  const [settledId, setSettledId] = useState<string | null>(null);

  const needsFetch = organizationId !== null && fromCatalog === null;
  const isLoading = needsFetch && settledId !== organizationId;

  useEffect(() => {
    if (!needsFetch || !organizationId) return;
    let cancelled = false;

    OrganizationsService.getOrganizationById(organizationId)
      .then((res) => {
        if (!cancelled) setFetched({ id: organizationId, organization: res.data });
      })
      .catch(() => {
        // The name and the program stay unknown; the page falls back to "cannot decide" and says so rather
        // than reading the wallet of an organization it knows nothing about.
        if (!cancelled) setFetched(null);
      })
      .finally(() => {
        if (!cancelled) setSettledId(organizationId);
      });

    return () => {
      cancelled = true;
    };
  }, [needsFetch, organizationId]);

  const organization = fromCatalog ?? (fetched?.id === organizationId ? fetched.organization : null);

  return { organization, isLoading };
}
